import { ChangeEvent, FormEvent, useCallback, useEffect, useRef, useState } from "react"
import { useDrag, useDrop } from "react-dnd/dist/hooks/index.js"
import {
    addRestaurantMenuItemImageUrl,
    deleteRestaurantMenuImage,
    getRestaurantMenuItemImages,
    IRestaurantMenuItemImage,
    reorderRestaurantMenuItemImages,
    uploadRestaurantMenuItemImage,
} from "@/shared/api/restaurant-menus"
import notify from "@/shared/lib/notify"
import { ConfirmModal } from "@/shared/ui/confirm-modal"
import { Loader } from "@/shared/ui/loader"
import { Modal } from "@/shared/ui/modal"
import { getApiErrorMessage } from "../lib/get-api-error-message"
import { RestaurantMenuImagesTarget } from "../model/types"

interface MenuImagesModalProps {
    isOpen: boolean
    menuId: string
    target: RestaurantMenuImagesTarget | null
    onClose: () => void
    onChanged: () => Promise<void>
}

interface DraggedImage {
    index: number
}

interface ImageTargetGeneration {
    key: string | null
    generation: number
}

interface DraggableImageCardProps {
    image: IRestaurantMenuItemImage
    index: number
    isBusy: boolean
    onDelete: (image: IRestaurantMenuItemImage) => void
    onDrop: (fromIndex: number, toIndex: number) => void
}

const imageDragType = "restaurant-menu-image"
const acceptedMimeTypes = ["image/png", "image/jpeg", "image/webp"]
const acceptedExtensions = [".png", ".jpg", ".jpeg", ".webp"]
const maxImageSize = 8 * 1024 * 1024

const sortImages = (images: IRestaurantMenuItemImage[]): IRestaurantMenuItemImage[] => {
    return [...images].sort((first, second) => (first.sort_order ?? 0) - (second.sort_order ?? 0))
}

const moveImage = (
    images: IRestaurantMenuItemImage[],
    fromIndex: number,
    toIndex: number
): IRestaurantMenuItemImage[] => {
    const reorderedImages = [...images]
    const [movedImage] = reorderedImages.splice(fromIndex, 1)
    reorderedImages.splice(toIndex, 0, movedImage)
    return reorderedImages
}

const isValidImageType = (file: File): boolean => {
    const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`
    return acceptedExtensions.includes(extension) || acceptedMimeTypes.includes(file.type)
}

const DraggableImageCard = ({
    image,
    index,
    isBusy,
    onDelete,
    onDrop,
}: DraggableImageCardProps) => {
    const cardRef = useRef<HTMLDivElement>(null)
    const [{ isDragging }, drag] = useDrag(
        () => ({
            type: imageDragType,
            item: { index } satisfies DraggedImage,
            canDrag: Boolean(image.id) && !isBusy,
            collect: (monitor) => ({
                isDragging: monitor.isDragging(),
            }),
        }),
        [image.id, index, isBusy]
    )
    const [{ isOver, canDrop }, drop] = useDrop(
        () => ({
            accept: imageDragType,
            canDrop: (draggedImage: DraggedImage) => !isBusy && draggedImage.index !== index,
            drop: (draggedImage: DraggedImage) => {
                if (!isBusy) {
                    onDrop(draggedImage.index, index)
                }
            },
            collect: (monitor) => ({
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop(),
            }),
        }),
        [index, isBusy, onDrop]
    )

    drag(drop(cardRef))

    return (
        <div
            ref={cardRef}
            className={[
                "rounded-lg border p-3 transition",
                isOver && canDrop ? "border-color-dark bg-[#F3F7F6]" : "border-[#D9E3E1]",
                isDragging ? "opacity-40" : "",
                image.id && !isBusy ? "cursor-grab active:cursor-grabbing" : "",
            ].join(" ")}
        >
            <div className="mb-2 flex items-center justify-between gap-3 text-sm">
                <span className="font-medium">Порядковый номер: {index + 1}</span>
                <span className="text-[#828282]">Перетащите для изменения порядка</span>
            </div>
            <div className="mb-2 h-32 overflow-hidden rounded-lg bg-[#F7F8FA]">
                {image.url ? (
                    <img src={image.url} alt={`Изображение ${index + 1}`} className="h-full w-full object-cover" />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-[#828282]">—</div>
                )}
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    disabled={!image.id || isBusy}
                    onClick={() => onDelete(image)}
                    className="form_cancel_button !h-9 !w-auto px-4 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    Удалить
                </button>
            </div>
        </div>
    )
}

export const MenuImagesModal = ({
    isOpen,
    menuId,
    target,
    onClose,
    onChanged,
}: MenuImagesModalProps) => {
    const [images, setImages] = useState<IRestaurantMenuItemImage[] | null>(null)
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [file, setFile] = useState<File | null>(null)
    const [url, setUrl] = useState("")
    const [formError, setFormError] = useState<string | null>(null)
    const [reorderError, setReorderError] = useState<string | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isReordering, setIsReordering] = useState(false)
    const [imageToDelete, setImageToDelete] = useState<IRestaurantMenuItemImage | null>(null)
    const imageListRequestId = useRef(0)
    const currentTargetKey = isOpen && target ? `${menuId}:${target.itemId}` : null
    const targetGeneration = useRef<ImageTargetGeneration>({
        key: currentTargetKey,
        generation: 0,
    })

    if (targetGeneration.current.key !== currentTargetKey) {
        targetGeneration.current = {
            key: currentTargetKey,
            generation: targetGeneration.current.generation + 1,
        }
    }

    const isCurrentTarget = useCallback((requestTarget: ImageTargetGeneration): boolean => {
        return (
            requestTarget.key !== null &&
            requestTarget.key === targetGeneration.current.key &&
            requestTarget.generation === targetGeneration.current.generation
        )
    }, [])

    const fetchImages = useCallback(async () => {
        if (!isOpen || !target) {
            return
        }

        const requestTarget = targetGeneration.current
        const requestId = ++imageListRequestId.current

        try {
            setIsLoading(true)
            setError(null)
            setImages(null)
            const response = await getRestaurantMenuItemImages(menuId, target.itemId)

            if (!isCurrentTarget(requestTarget) || requestId !== imageListRequestId.current) {
                return
            }

            setImages(sortImages(response.data))
        } catch (requestError) {
            if (!isCurrentTarget(requestTarget) || requestId !== imageListRequestId.current) {
                return
            }

            setImages(null)
            setError(getApiErrorMessage(requestError))
        } finally {
            if (isCurrentTarget(requestTarget) && requestId === imageListRequestId.current) {
                setIsLoading(false)
            }
        }
    }, [isCurrentTarget, isOpen, menuId, target])

    useEffect(() => {
        if (!isOpen) {
            setImages(null)
            setIsLoading(false)
            setError(null)
            setFile(null)
            setUrl("")
            setFormError(null)
            setReorderError(null)
            setIsReordering(false)
            setImageToDelete(null)
            return
        }

        void fetchImages()
    }, [fetchImages, isOpen])

    const getAppendSortOrder = (loadedImages: IRestaurantMenuItemImage[]): number => {
        if (loadedImages.length === 0) {
            return 0
        }

        return Math.max(...loadedImages.map((image) => image.sort_order ?? -1)) + 1
    }

    const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        setFormError(null)
        setFile(event.target.files?.[0] ?? null)
    }

    const handleUploadFile = async (event: FormEvent) => {
        event.preventDefault()

        if (!images || isLoading) {
            setFormError("Сначала дождитесь загрузки списка изображений")
            return
        }

        if (!target || !file) {
            setFormError("Выберите файл")
            return
        }

        if (!isValidImageType(file)) {
            setFormError("Допустимы только PNG, JPG, JPEG, WEBP")
            return
        }

        if (file.size > maxImageSize) {
            setFormError("Файл больше 8 МБ")
            return
        }

        try {
            setIsSubmitting(true)
            setFormError(null)
            await uploadRestaurantMenuItemImage(menuId, target.itemId, file, getAppendSortOrder(images))
            notify.success("Изображение добавлено")
            setFile(null)
            await fetchImages()
            await onChanged()
        } catch (requestError) {
            setFormError(getApiErrorMessage(requestError))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleAddUrl = async (event: FormEvent) => {
        event.preventDefault()

        if (!images || isLoading) {
            setFormError("Сначала дождитесь загрузки списка изображений")
            return
        }

        if (!target) {
            return
        }

        const normalizedUrl = url.trim()

        if (!normalizedUrl) {
            setFormError("Укажите URL")
            return
        }

        try {
            setIsSubmitting(true)
            setFormError(null)
            await addRestaurantMenuItemImageUrl(menuId, target.itemId, {
                url: normalizedUrl,
                sort_order: getAppendSortOrder(images),
            })
            notify.success("Изображение добавлено")
            setUrl("")
            await fetchImages()
            await onChanged()
        } catch (requestError) {
            setFormError(getApiErrorMessage(requestError))
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleReorder = useCallback(async (fromIndex: number, toIndex: number) => {
        if (!target || !images || fromIndex === toIndex || isReordering || isSubmitting) {
            return
        }

        const previousImages = images
        const reorderedImages = moveImage(previousImages, fromIndex, toIndex)
        const orderedIds = reorderedImages.map((image) => image.id)
        const requestTarget = targetGeneration.current

        if (orderedIds.some((id) => !id)) {
            setReorderError("Не удалось изменить порядок: у изображения нет идентификатора")
            return
        }

        try {
            setIsReordering(true)
            setReorderError(null)
            setImages(reorderedImages)
            const response = await reorderRestaurantMenuItemImages(
                menuId,
                target.itemId,
                orderedIds as string[]
            )

            if (!isCurrentTarget(requestTarget)) {
                return
            }

            setImages(response.data)
            await onChanged()
        } catch (requestError) {
            if (isCurrentTarget(requestTarget)) {
                setImages(previousImages)
                setReorderError(getApiErrorMessage(requestError))
            }
        } finally {
            if (isCurrentTarget(requestTarget)) {
                setIsReordering(false)
            }
        }
    }, [images, isCurrentTarget, isReordering, isSubmitting, menuId, onChanged, target])

    const handleConfirmDelete = async () => {
        if (!imageToDelete?.id) {
            return
        }

        try {
            await deleteRestaurantMenuImage(menuId, imageToDelete.id)
            notify.success("Изображение удалено")
            setImageToDelete(null)
            await fetchImages()
            await onChanged()
        } catch (requestError) {
            notify.error(getApiErrorMessage(requestError))
        }
    }

    const canAddImages = images !== null && !isLoading && !error

    return (
        <>
            <Modal isOpen={isOpen} title={`Изображения: ${target?.title ?? ""}`} onRequestClose={onClose}>
                <div className="flex min-w-[640px] flex-col gap-4">
                    {isLoading && (
                        <div className="info_panel">
                            <Loader />
                        </div>
                    )}

                    {!isLoading && error && (
                        <div className="info_panel flex items-center justify-between gap-4">
                            <div className="text-color-danger">{error}</div>
                            <button type="button" onClick={() => void fetchImages()} className="form_handle_button !w-auto px-6">
                                Повторить
                            </button>
                        </div>
                    )}

                    {!isLoading && !error && images && images.length === 0 && (
                        <div className="info_panel">Изображений нет</div>
                    )}

                    {!isLoading && !error && images && images.length > 0 && (
                        <div className="grid grid-cols-2 gap-3" aria-busy={isReordering || isSubmitting}>
                            {images.map((image, index) => (
                                <DraggableImageCard
                                    key={image.id ?? image.url ?? `image-${index}`}
                                    image={image}
                                    index={index}
                                    isBusy={isReordering || isSubmitting}
                                    onDelete={setImageToDelete}
                                    onDrop={(fromIndex, toIndex) => void handleReorder(fromIndex, toIndex)}
                                />
                            ))}
                        </div>
                    )}

                    {isReordering && (
                        <div className="info_panel" role="status">
                            Сохраняем порядок изображений...
                        </div>
                    )}

                    {reorderError && (
                        <div className="info_panel !bg-[#FFF4F4] text-color-danger" role="alert">
                            {reorderError}
                        </div>
                    )}

                    <div className="rounded-lg border border-[#D9E3E1] p-4">
                        <div className="mb-3 font-medium">Добавить файл</div>
                        <form onSubmit={handleUploadFile} className="flex flex-col gap-3">
                            <input
                                type="file"
                                accept=".png,.jpg,.jpeg,.webp"
                                disabled={!canAddImages || isSubmitting || isReordering}
                                onChange={handleFileChange}
                            />
                            <div className="text-sm text-[#828282]">
                                {canAddImages
                                    ? "Порядковый номер будет назначен автоматически в конце списка."
                                    : "Добавление станет доступно после успешной загрузки списка изображений."}
                            </div>
                            <button type="submit" disabled={!canAddImages || isSubmitting || isReordering} className="form_handle_button !w-auto px-6">
                                Загрузить
                            </button>
                        </form>
                    </div>

                    <div className="rounded-lg border border-[#D9E3E1] p-4">
                        <div className="mb-3 font-medium">Добавить по URL</div>
                        <form onSubmit={handleAddUrl} className="flex flex-col gap-3">
                            <input
                                value={url}
                                onChange={(event) => setUrl(event.target.value)}
                                className="input_field"
                                placeholder="https://..."
                                disabled={!canAddImages || isSubmitting || isReordering}
                            />
                            <div className="text-sm text-[#828282]">
                                {canAddImages
                                    ? "Порядковый номер будет назначен автоматически в конце списка."
                                    : "Добавление станет доступно после успешной загрузки списка изображений."}
                            </div>
                            <button type="submit" disabled={!canAddImages || isSubmitting || isReordering} className="form_handle_button !w-auto px-6">
                                Добавить
                            </button>
                        </form>
                    </div>

                    {formError && <div className="text-color-danger">{formError}</div>}
                </div>
            </Modal>

            <ConfirmModal
                isOpen={Boolean(imageToDelete)}
                title="Удалить изображение"
                message="Изображение будет удалено без возможности восстановления. Продолжить?"
                confirmText="Удалить"
                onConfirm={handleConfirmDelete}
                onCancel={() => setImageToDelete(null)}
            />
        </>
    )
}
